import { useEffect } from "react";

import dayjs from "dayjs";
import PropTypes from "prop-types";
import { FormProvider, useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import Grid from "@mui/material/Grid";

import { computeOrderTotals } from "@/features/shared";
import { CenteredLoading } from "@/features/ui";
import { AddLensProductSection } from "./add-lens-product-section/add-lens-product-section";
import { GeneralInformationSection } from "./general-information-section/general-information-section";
import { LensProductItem } from "./lens-product-item";
import { OtherSection } from "./other-section";
import { PriceSummarySection } from "./price-summary-section";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./purchase-order-lab-form-constants";

export const PurchaseOrderLabForm = ({
  defaultValues,
  onSubmit,
  renderActionButton,
  editMode = false,
  isLoading = false,
}) => {
  const methods = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });
  const { reset, handleSubmit, formState } = methods;

  useEffect(() => {
    if (!defaultValues) {
      return;
    }

    reset({ ...DEFAULT_VALUES, ...defaultValues });
  }, [defaultValues, reset]);

  const onFormSubmit = async (formData) => {
    const { taxAmount, grandTotal } = computeOrderTotals(formData);

    const payload = {
      taxable: formData.taxable,
      totalIncludesTax: formData.taxable && formData.totalIncludesTax,
      transactionDate: dayjs(formData.transactionDate).format("YYYY-MM-DD"),
      warehouseId: formData.warehouse.id,
      sellerableId: formData.seller.id,
      sellerableType: formData.sellerableType,
      paymentTermId: formData.paymentTerm.id,
      note: formData.note || null,
      taxAmount,
      taxPercentage: formData.taxPercentage,
      discountType: formData.discountType,
      discountAmount: formData.discountInput || 0,
      grandTotal,
      purchaseOrderProductVariants: (() => {
        const buildPrescriptionNote = (side) => {
          const data = {};
          if (side.axis) data.axis = side.axis;
          if (side.pd) data.pd = side.pd;
          if (side.prism) data.prism = side.prism;

          if (formData.productAddOn?.name) {
            data.addOn = formData.productAddOn.name;
          }

          return Object.keys(data).length > 0 ? JSON.stringify(data) : null;
        };

        const sides = ["leftSide", "rightSide"].filter(
          (sideKey) =>
            formData[sideKey]?.enabled && formData[sideKey]?.productVariant?.id
        );

        const perUnitCost = formData.subtotal / sides.length;

        return sides.map((sideKey) => {
          const side = formData[sideKey];

          return {
            sku: side.productVariant.sku,
            quantity: 1,
            discount: 0,
            costPerUnit: perUnitCost,
            side: sideKey === "leftSide" ? "LEFT" : "RIGHT",
            note: buildPrescriptionNote(side),
          };
        });
      })(),
    };

    await onSubmit(payload);
  };

  return isLoading ? (
    <CenteredLoading sx={{ height: "100vh" }} />
  ) : (
    <FormProvider {...methods}>
      <Grid
        container
        component="form"
        noValidate
        onSubmit={handleSubmit(onFormSubmit)}
        spacing={2.5}
      >
        <Grid size={{ xs: 12 }}>
          <GeneralInformationSection editMode={editMode} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          {editMode ? <LensProductItem /> : <AddLensProductSection />}
        </Grid>
        <Grid size={{ xs: 12, md: 5, lg: 7 }}>
          <OtherSection editMode={editMode} />
        </Grid>
        <Grid size={{ xs: 12, md: 7, lg: 5 }}>
          <PriceSummarySection editMode={editMode} />
        </Grid>
        <Grid
          size={{ xs: 12 }}
          sx={{
            display: "flex",
            justifyContent: "flex-end"
          }}>
          {renderActionButton({ isSubmitting: formState.isSubmitting })}
        </Grid>
      </Grid>
    </FormProvider>
  );
};

PurchaseOrderLabForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
  renderActionButton: PropTypes.func,
  editMode: PropTypes.bool,
  isLoading: PropTypes.bool,
};
